import React from 'react'
import { Link } from 'react-router-dom'
import { FaSearch, FaUsers, FaGlobe, FaBolt } from 'react-icons/fa'
import AnimatedCount from './AnimatedCount.jsx'
import SEO from './SEO.jsx'

export default function About() {
  return (
    <div className="bg-brand-bg text-brand-text min-h-screen">
      <SEO
        title="Why Websites Still Matter in 2026 | 2merative"
        description="Your website is your digital storefront, open 24/7, visible to the world. Learn why owning your space online builds trust, drives sales, and secures your future."
        path="/about"
        image="/og-image.png"
      />

      <section className="bg-gradient-to-b from-[#eef5ff] via-brand-bg to-brand-surface pt-32 pb-section">
        <div className="max-w-[1180px] mx-auto px-6 text-center">
          <h1 className="text-[clamp(2.8rem,6vw,4.5rem)] font-bold leading-[1.05] tracking-[-0.03em] text-brand-heading mb-8">
            Why Websites Still Matter in 2026
          </h1>

          <p className="text-[1.12rem] leading-[1.85] text-brand-textSoft max-w-3xl mx-auto">
            Your website is your digital storefront, open 24/7, visible to the world. It’s not just about having one, it’s about owning your space online, controlling your message, and turning visitors into customers.
          </p>
        </div>
      </section>

      <section className="py-section bg-brand-surface">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="flex items-start gap-4 p-6 rounded-xl2 border border-brand-border bg-brand-surface shadow-card">
              <FaSearch className="text-brand-primary text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-brand-heading mb-1">
                  Be Found Online
                </h3>
                <p className="text-brand-textSoft text-sm leading-[1.7]">
                  Most people search online before buying. If you’re not visible, you’re invisible.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-xl2 border border-brand-border bg-brand-surface shadow-card">
              <FaUsers className="text-brand-primary text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-brand-heading mb-1">
                  Build Trust & Credibility
                </h3>
                <p className="text-brand-textSoft text-sm leading-[1.7]">
                  People trust businesses with a modern, professional website. It’s often your first impression.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-xl2 border border-brand-border bg-brand-surface shadow-card">
              <FaGlobe className="text-brand-primary text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-brand-heading mb-1">
                  Own Your Platform
                </h3>
                <p className="text-brand-textSoft text-sm leading-[1.7]">
                  Social media is rented space. A website is yours, fully in your control.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 rounded-xl2 border border-brand-border bg-brand-surface shadow-card">
              <FaBolt className="text-brand-primary text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-brand-heading mb-1">
                  Convert More Leads
                </h3>
                <p className="text-brand-textSoft text-sm leading-[1.7]">
                  Well-designed websites turn visitors into bookings, sales, and enquiries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section bg-gradient-to-b from-[#eef5ff] to-brand-surface">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="rounded-xl3 border border-brand-border bg-brand-surface p-10 shadow-cardStrong">
            <h3 className="text-2xl font-bold text-center text-brand-heading mb-10">
              The Numbers Don’t Lie
            </h3>

            <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 text-center">
              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={30} duration={2} suffix="%" />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  of new businesses fail in the first 2 years, most lack a website.
                </p>
              </div>

              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={81} duration={2.2} suffix="%" />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  of people research a business online before buying.
                </p>
              </div>

              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={75} duration={2.5} suffix="%" />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  of businesses see increased revenue within 6 months.
                </p>
              </div>

              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={2} duration={1.8} />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  new customers can cover the cost of a basic website.
                </p>
              </div>

              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={24} duration={2.5} suffix="/7" />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  your website works for you while you sleep.
                </p>
              </div>

              <div>
                <p className="text-4xl font-bold text-brand-primary">
                  <AnimatedCount end={70} duration={2.4} suffix="%" />
                </p>
                <p className="text-sm text-brand-textSoft mt-2">
                  of consumers trust businesses with a professional website.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section text-center">
        <div className="max-w-[900px] mx-auto px-6">
          <h3 className="text-xl font-semibold text-brand-heading mb-4">
            Want to see how a website could work for you?
          </h3>

          <Link
            to="/contact"
            className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-6 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
          >
            Let’s Talk
          </Link>
        </div>
      </section>
    </div>
  )
}